/**
 * Where a session stands, as the admin's sessions screen and a campaign's
 * session scheduler say it.
 *
 * Lifted out of use-world-session-browser.ts, which holds the screen's
 * queries, so the card's words can be tested on their own: a running session
 * whose match is gone reads as interrupted (the check is
 * nextInterruptionCheck's, in api/admin/sessions/helpers.ts).
 */

import type { SessionItem } from '../../api/admin/sessions/types';

/**
 * A draft, or a session with no start time yet — which the card dates "Not
 * scheduled" — is a draft, never "Scheduled" beside "Not scheduled". Old rows
 * predate the status column and read as scheduled once they have a start. A
 * running session whose world the game server runs no match on is
 * interrupted.
 */
export type SessionStatusView = 'draft' | 'scheduled' | 'in-progress' | 'interrupted' | 'ended' | 'cancelled';

export function getSessionStatus(session: Pick<SessionItem, 'status' | 'startTimeUtc'>, interrupted = false): SessionStatusView {
  switch (session.status) {
    case 'RUNNING':   return interrupted ? 'interrupted' : 'in-progress';
    case 'FINISHED':  return 'ended';
    case 'CANCELLED': return 'cancelled';
    case 'DRAFT':     return 'draft';
    default:          return session.startTimeUtc ? 'scheduled' : 'draft'; // SCHEDULED or missing (old data)
  }
}

/**
 * A session as the world's sessions have it now, when they hold it; else the
 * copy given. A campaign carries its gameplay sessions as it read them, while
 * the interruption check reads the world's sessions again every ten seconds
 * as one runs (useInterruptedSessionIds), so a session that ends while the
 * campaign is open says so, and the check stops with it.
 */
export function sessionNow<S extends Pick<SessionItem, 'id' | 'status' | 'startTimeUtc'>>(
  copy: S,
  worldSessions: readonly SessionItem[] | undefined,
): S | SessionItem {
  return worldSessions?.find((s) => s.id === copy.id) ?? copy;
}
